import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  AlertTriangle, 
  RefreshCw,
  Eye,
  Filter,
  Languages,
  FileX,
  FileWarning,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const ISSUE_TYPES = [
  { id: 'wrong_language', name: 'Wrong Language', icon: Languages },
  { id: 'missing_content', name: 'Missing Content', icon: FileX },
  { id: 'improper_format', name: 'Improper Format', icon: FileWarning },
  { id: 'wrong_content', name: 'Wrong Content', icon: FileText },
];

export default function Issues() {
  const { api } = useAuth();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    fetchIssues();
  }, [typeFilter, severityFilter]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter && typeFilter !== 'all') params.append('issue_type', typeFilter);
      if (severityFilter && severityFilter !== 'all') params.append('severity', severityFilter);

      const response = await api().get(`/issues?${params.toString()}`);
      setIssues(response.data);
    } catch (error) {
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  const getIssueIcon = (type) => {
    const config = ISSUE_TYPES.find(t => t.id === type);
    return config?.icon || AlertTriangle;
  };

  const getIssueTypeBadgeClass = (type) => {
    const classes = {
      wrong_language: 'bg-amber-100 text-amber-800',
      missing_content: 'bg-red-100 text-red-800',
      improper_format: 'bg-indigo-100 text-indigo-800',
      wrong_content: 'bg-pink-100 text-pink-800',
    };
    return classes[type] || 'bg-gray-100 text-gray-800';
  };

  const getSeverityBadgeClass = (severity) => {
    const classes = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-amber-100 text-amber-800 border-amber-200',
      low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    };
    return classes[severity] || 'bg-gray-100 text-gray-800';
  };

  const formatIssueType = (type) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group issues by type for summary
  const issueCounts = issues.reduce((acc, issue) => {
    acc[issue.issue_type] = (acc[issue.issue_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fade-in" data-testid="issues-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">Issues</h1>
          <p className="text-muted-foreground mt-1">Documents with validation problems</p>
        </div>
        <Button onClick={fetchIssues} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Issue Type Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {ISSUE_TYPES.map((type) => {
          const Icon = type.icon;
          const count = issueCounts[type.id] || 0;
          return (
            <Card 
              key={type.id}
              className={`cursor-pointer transition-all ${typeFilter === type.id ? 'ring-2 ring-primary' : 'hover:bg-muted/30'}`}
              onClick={() => setTypeFilter(typeFilter === type.id ? 'all' : type.id)}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">{type.name}</p>
                    <p className="text-2xl font-bold font-mono mt-1">{count}</p>
                  </div>
                  <Icon className={`w-6 h-6 ${count > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Issue Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ISSUE_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="severity-filter">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Issues Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            All Issues ({issues.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : issues.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Issue Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issues.map((issue) => {
                    const Icon = getIssueIcon(issue.issue_type);
                    return (
                      <TableRow key={issue.id} data-testid={`issue-row-${issue.id}`}>
                        <TableCell className="font-medium">
                          <Link 
                            to={`/documents/${issue.document_id}`}
                            className="hover:text-primary hover:underline flex items-center gap-2"
                          >
                            <Icon className="w-4 h-4" />
                            {issue.document_name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge className={getIssueTypeBadgeClass(issue.issue_type)}>
                            {formatIssueType(issue.issue_type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate text-sm text-muted-foreground">
                          {issue.description}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getSeverityBadgeClass(issue.severity)}>
                            {issue.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground font-mono">
                          {formatDate(issue.created_at)}
                        </TableCell>
                        <TableCell>
                          <Link to={`/documents/${issue.document_id}`}>
                            <Button variant="ghost" size="icon" data-testid={`view-issue-${issue.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-medium mb-2">No issues found</h3>
              <p className="text-sm text-muted-foreground">
                {typeFilter !== 'all' || severityFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'All documents have passed validation'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
